import React from 'react';
import styled, { keyframes } from 'styled-components';
import SoundControls from './SoundControls';
import ThemeSelector from './ThemeSelector';
import { useVersionInfo } from '../context/VersionContext';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../i18n';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  /** Navigera till startsidan (manager) */
  onHome?: () => void;
  /** Indikerar om ett spel pågår (används för varning) */
  activeGame?: boolean;
  /** Aktivera/inaktivera AI-assistenten */
  aiEnabled?: boolean;
  onToggleAi?: () => void;
  /** Aktivera/inaktivera partikeleffekter */
  effectsEnabled?: boolean;
  onToggleEffects?: () => void;
  /** Skaka telefonen för att kasta */
  shakeToRollEnabled?: boolean;
  onToggleShakeToRoll?: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 1200;
  display: ${p => p.$isOpen ? 'block' : 'none'};
  animation: ${fadeIn} 0.25s ease;
`;

const Drawer = styled.div`
  --mm-bg: var(--theme-card-background,#121212);
  --mm-text: var(--theme-text,#f5f5f5);
  --mm-border: var(--theme-card-border, rgba(255,255,255,0.15));
  position: fixed;
  top: clamp(8px, env(safe-area-inset-top) + 8px, 24px);
  right: 0;
  bottom: 0;
  width: min(60%, 280px);
  background: var(--mm-bg);
  color: var(--mm-text);
  box-shadow: -2px 0 18px rgba(0,0,0,0.5);
  padding: 24px 20px 40px;
  overflow-y: auto;
  animation: ${slideIn} 0.25s ease;
  display: flex;
  flex-direction: column;
  gap: 26px;
  border-top-left-radius: 18px;  /* Ändrat till left */
  border-bottom-left-radius: 18px;  /* Ändrat till left */

  @media (min-width: 640px) {
    top: 40px;
    width: 320px;
    border-top-left-radius: 22px;  /* Ändrat till left */
    border-bottom-left-radius: 22px;  /* Ändrat till left */
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--mm-text);
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: var(--theme-text, #fff);
  transition: opacity .2s;
  &:hover { opacity: .75; }
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--theme-accent, var(--theme-secondary, #4dabf7));
  opacity: .92;
  margin-top: 2px;
  text-shadow: 0 1px 1px rgba(0,0,0,0.25);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255,255,255,0.12);
  margin: 6px 0 14px;
`;

const VersionBox = styled.div`
  --vb-bg: var(--theme-card-background-alt, rgba(255,255,255,0.06));
  background: var(--vb-bg);
  border: 1px solid var(--mm-border);
  padding: 14px 16px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--mm-text);
  box-shadow: inset 0 0 0 999px rgba(0,0,0,0.02);
`;

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onHome, activeGame, aiEnabled, onToggleAi, effectsEnabled, onToggleEffects, shakeToRollEnabled, onToggleShakeToRoll }) => {
  const { showConfirmationToast } = useToast();
  const { t, locale, setLocale } = useI18n();
  // Always call hook normally (provider assumed present in app root). Fallback if fields missing.
  const { info, loading, error, refresh } = useVersionInfo();
  const handleAiToggle = onToggleAi ?? (() => {});
  const handleEffectsToggle = onToggleEffects ?? (() => {});
  const handleShakeToRollToggle = onToggleShakeToRoll ?? (() => {});
  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Drawer onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Meny</Title>
          <CloseBtn aria-label="Stäng meny" onClick={onClose}>×</CloseBtn>
        </Header>
        {onHome && (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <button
              onClick={() => {
                if (activeGame) {
                  showConfirmationToast('Ett spel pågår. Gå till startsidan och avbryta nuvarande spel?', {
                    onConfirm: () => {
                      try { onHome(); } catch {}
                      onClose();
                    },
                    confirmText: 'Ja, avbryt spel',
                    cancelText: 'Nej'
                  });
                  return;
                }
                try { onHome(); } catch {}
                onClose();
              }}
              style={{
                background:'linear-gradient(135deg,var(--theme-primary,#4caf50),var(--theme-secondary,#2196f3))',
                border:'1px solid rgba(255,255,255,0.25)',
                color:'#fff',
                fontWeight:600,
                padding:'12px 16px',
                borderRadius:14,
                fontSize:15,
                cursor:'pointer',
                display:'flex',
                alignItems:'center',
                gap:10,
                boxShadow:'0 4px 12px -2px rgba(0,0,0,0.45)'
              }}
            >🏠 <span>Start / Hem</span></button>
            <Divider />
          </div>
        )}
        <div>
          <SectionLabel>Spelinställningar</SectionLabel>
          <Divider />
          <div style={{display:'grid',gap:10}}>
            <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <span>AI-assistent</span>
              <input type="checkbox" checked={!!aiEnabled} onChange={handleAiToggle} disabled={!onToggleAi} />
            </label>
            <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <span>Partikeleffekter</span>
              <input type="checkbox" checked={!!effectsEnabled} onChange={handleEffectsToggle} disabled={!onToggleEffects} />
            </label>
            <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <span>Skaka för att kasta</span>
              <input type="checkbox" checked={!!shakeToRollEnabled} onChange={handleShakeToRollToggle} disabled={!onToggleShakeToRoll} />
            </label>
          </div>
        </div>
        <div>
          <SectionLabel>Ljud & Musik</SectionLabel>
          <Divider />
          <SoundControls />
        </div>
        <div>
          <SectionLabel>Teman</SectionLabel>
          <Divider />
          <ThemeSelector />
        </div>
        <div>
          {/* CHANGE (2026-08-15): Språkväljare (svenska default, engelska opt-in).
              Sekundära ytor är fortfarande svenska – se docs/I18N.md. */}
          <SectionLabel>{t('menu.language')}</SectionLabel>
          <Divider />
          <div style={{ display: 'flex', gap: 8 }}>
            {([['sv', '🇸🇪 Svenska'], ['en', '🇬🇧 English']] as const).map(([loc, label]) => (
              <button
                key={loc}
                onClick={() => setLocale(loc)}
                aria-pressed={locale === loc}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: locale === loc ? '2px solid var(--theme-primary,#4caf50)' : '1px solid var(--mm-border, rgba(0,0,0,0.15))',
                  background: locale === loc ? 'var(--theme-primary,#4caf50)' : 'transparent',
                  color: locale === loc ? '#fff' : 'var(--mm-text, inherit)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel>Version</SectionLabel>
          <Divider />
          <VersionBox>
            {loading && <div>Laddar version...</div>}
            {error && <div style={{color:'#b91c1c'}}>{error}</div>}
            {info && (
              <>
                <strong>Version:</strong> {info.version}<br />
                {info.date && <><strong>Datum:</strong> {new Date(info.date).toLocaleString()}<br /></>}
                {info.changes && info.changes.length > 0 && (
                  <details style={{marginTop:6}}>
                    <summary style={{cursor:'pointer'}}>Ändringar ({info.changes.length})</summary>
                    <ul style={{margin:'6px 0 0 18px',padding:0}}>
                      {info.changes.slice(0,10).map((c: string, i: number)=>(<li key={i}>{c}</li>))}
                    </ul>
                  </details>
                )}
                <button
                  onClick={refresh}
                  style={{
                    marginTop:8,
                    background:'var(--theme-primary,#2563eb)',
                    border:'1px solid rgba(255,255,255,0.25)',
                    padding:'6px 12px',
                    borderRadius:8,
                    fontSize:12,
                    cursor:'pointer',
                    color:'#fff',
                    fontWeight:600
                  }}
                >Uppdatera info</button>
              </>
            )}
          </VersionBox>
        </div>
      </Drawer>
    </Overlay>
  );
};

export default MobileMenu;
